import {createProject} from './api'
import type {CreateProjectFormValues} from './create-project-form-values'
import type {CreateProjectBillingTermsInput, CreateProjectInput} from './schemas'

export function toBillingTermsInput(
    values: CreateProjectFormValues,
): CreateProjectBillingTermsInput {
    return {
        billingMethod: values.billingMethod,
        transportationHourlyRate:
            values.billingMethod === 'hourly_transportation'
                ? values.billingTerms.transportationHourlyRate
                : null,
        estimatedTransportationHours:
            values.billingMethod === 'hourly_transportation'
                ? (values.billingTerms.estimatedTransportationHours ?? null)
                : null,
        flatRateAmount:
            values.billingMethod === 'flat_rate' ? values.billingTerms.flatRateAmount : null,
        ratePerLoad: values.billingMethod === 'per_load' ? values.billingTerms.ratePerLoad : null,
        perLoadNotes: values.billingMethod === 'per_load' ? values.billingTerms.perLoadNotes : null,
        // Demurrage is encompassed by the transportation hourly rate for hourly
        // projects, so it is never submitted for them. The form value is retained so
        // toggling the billing method back doesn't lose what the user entered.
        demurrageHourlyRate:
            values.billingMethod === 'hourly_transportation'
                ? null
                : values.billingTerms.demurrageHourlyRate,
        disposalFees: values.billingTerms.disposalFees.map((fee) => ({
            wasteType: fee.wasteType,
            unit: fee.unit,
            unitRate: fee.unitRate ?? 0,
            estimatedQuantity: fee.estimatedQuantity,
        })),
        washoutFee: values.billingTerms.washoutFee,
        hseiPercent: values.billingTerms.hseiPercent,
    }
}

function toNewOrderLineItems(values: CreateProjectFormValues): CreateProjectInput['lineItems'] {
    return values.manualBillableLineItems
        .filter((lineItem) => lineItem.name.trim())
        .map((lineItem) => {
            const quantity = lineItem.quantity ?? 0
            const unitPrice = lineItem.unitPrice ?? 0
            return {
                chargeType: 'manual',
                name: lineItem.name,
                description: lineItem.description,
                quantity,
                unit: lineItem.unit,
                unitPrice,
                totalPrice: quantity * unitPrice,
            }
        })
}

export async function submitProject(values: CreateProjectFormValues): Promise<string> {
    const project = await createProject({
        customerId: values.customerId!,
        name: values.name,
        description: values.description,
        notes: values.notes,
        internalContactId: values.internalContactId!,
        customerContactId: values.customerContactId,
        billingTerms: toBillingTermsInput(values),
        lineItems: toNewOrderLineItems(values),
    })
    return project.id
}
